<template>
  <div v-if="venueCampaign && activeVenue">
    <popup-apply-email-template ref="popupApplyEmailTemplate" />
    <vx-card no-shadow>
      <!-- If the campaign has no emailjson yet, show the templates -->
      <vs-row v-if="!venueCampaign.isBasedOnMasterCampaign && !venueCampaign.emailJson">
        <vs-row>
          <h2 class="my-4">Email</h2>
        </vs-row>

        <div v-if="!emailTemplates" class="flex flex-col justify-center items-center mt-12 pt-12">
          <span class="flex h-6 w-6 mb-5">
            <span class="animate-ping absolute inline-flex h-6 w-6 rounded-full bg-primary opacity-75"></span>
            <span class="relative inline-flex rounded-full h-6 w-6 bg-primary"></span>
          </span>
          <span> Loading</span>
        </div>
        <div v-else>
          <vs-row>
            <div class="mb-2">Select an e-mail template to start from. You can change this later.</div>
          </vs-row>

          <vs-row>
            <vs-card class="max-w-sm mr-6" v-for="template in emailTemplates" :key="template.id">
              <div slot="media">
                <div class="previewImageWrapper">
                  <img
                    @click.stop.prevent
                    v-img:emailTemplate
                    class="previewImage rounded-lg"
                    :src="template.previewImage"
                  />
                </div>
              </div>
              <div class="w-full flex justify-between items-center">
                <h4>{{ template.name }}</h4>
                <vs-button @click="applyTemplate(template)">Select</vs-button>
              </div>
            </vs-card>
          </vs-row>
        </div>
      </vs-row>

      <div v-else>
        <div class="mb-6 flex">
          <div class="flex-grow flex-shrink-1 pr-4">
            <h2 class="my-4">Email</h2>
            <h5 class="my-5" v-if="!isEditable">
              <b
                >Note: Cannot be saved as campaign has status
                {{ statusObject(venueCampaign.status).text }}.</b
              >
            </h5>

            <div class="mb-5">
              <!-- <p class="mb-0" v-if="venueCampaign.isBasedOnMasterCampaign">
                The HQ campaign design has been modified to match the design of this venue ({{
                  activeVenue.name
                }}).<br />
              </p> -->
              <p class="b-5 max-w-xl">
                <!-- You can specify dynamic content by using the <b>Merge Tags</b> feature in the mail editor.<br /> -->
                This is your e-mail campaign. You can use the flexible drag and drop e-email editor below to
                customize your e-mail exactly as you see fit for your venue. Remember, however, to follow
                REKOMs guidelines as well as always include your venue details and unsubscribe link in the
                bottom of your e-mail. <b>Merge tags</b> are available in the e-mail header as well as e-mail
                layout.
              </p>
            </div>

            <div class="vx-row mb-6">
              <div class="vx-col sm:w-1/5 w-full">
                <span>Email subject</span>
              </div>
              <div class="vx-col sm:w-4/5 w-full max-w-xl">
                <vs-input
                  :placeholder="'Mail from {{venue_name}}'"
                  v-model="venueCampaign.emailSubject"
                  class="w-full"
                />
              </div>
            </div>
          </div>
          <div class="flex justify-end flex-shrink-0">
            <vx-card class="max-w-md">
              <h5 class="mb-1">Checklist</h5>
              <div class="mb-3 pr-3">The email must contain the following:</div>

              <div class="ml-2">
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.email_subject ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.email_subject }"
                    >Email subject</span
                  >
                </vs-row>
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.venue_name ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.venue_name }"
                    >Venue name</span
                  >
                </vs-row>
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.venue_vat ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.venue_vat }">Venue VAT</span>
                </vs-row>
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.venue_address ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.venue_address }"
                    >Venue address</span
                  >
                </vs-row>
                <vs-row>
                  <feather-icon
                    :icon="requiredTags.venue_unsubscribe_link ? 'CheckSquareIcon' : 'SquareIcon'"
                    class="inline-block mr-2"
                    svgClasses="w-6 h-6"
                  />
                  <span class="font-bold" :class="{ 'line-through': requiredTags.venue_unsubscribe_link }"
                    >Venue unsubscribe link</span
                  >
                </vs-row>
              </div>
              <vs-progress :height="8" :percent="requiredTags.progress" class="mt-2" color="#24c1a0"
                >success</vs-progress
              >
            </vx-card>
          </div>
        </div>

        <email-template-editor
          :email-json="venueCampaign.emailJson"
          :venue-logo-url="venueCampaign.venueLogoUrl"
          :venue-style="activeVenue.style"
          ref="emailEditor"
        />

        <!-- Save & Remove Button -->
        <vs-row
          vs-justify="space-between"
          class="flex flex-wrap items-end"
          style="margin-top: 32px"
          v-if="hasPermissionForActiveVenue('vp.vp.venuecampaigns.edit')"
        >
          <vs-button v-if="isEditable" id="template-button" @click="onClickApplyTemplate" class="mt-2">
            Apply email template
          </vs-button>

          <div>
            <vs-button v-if="isEditable" @click="onClickSendTest" class="mt-2">Send test</vs-button>
            <vs-button
              v-if="isEditable"
              :disabled="!requiredTags.isValid"
              id="save-button"
              @click="onClickSave"
              class="mt-2 ml-4"
              >Save Changes</vs-button
            >
          </div>
        </vs-row>
      </div>
    </vx-card>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";

import EmailTemplateEditor from "@/components/EmailTemplateEditor/EmailTemplateEditor.vue";
import campaignMixin from "@/views/mixins/campaignMixin";
import PopupApplyEmailTemplate from "./PopupApplyEmailTemplate.vue";
import { ClientSideRowModelSteps } from "ag-grid-community";

export default {
  components: {
    EmailTemplateEditor,
    PopupApplyEmailTemplate,
  },
  mixins: [campaignMixin],
  props: ["venueCampaign", "isEditable"],
  data() {
    return {
      tempDesign: "",
      designInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      consentTypes: "templates/consentTypes",
      activeVenue: "venues/activeVenue",
      emailTemplates: "emailTemplates/emailTemplatesForCampaigns",
    }),
    requiredTags() {
      const result = {
        email_subject: this.venueCampaign.emailSubject.length > 0,
        venue_name: this.tempDesign.includes("{{venue_name}}"),
        venue_vat: this.tempDesign.includes("{{venue_vat}}"),
        venue_address: this.tempDesign.includes("{{venue_address}}"),
        venue_unsubscribe_link: this.tempDesign.includes("{{venue_guest_unsub_link}}"),
      };

      return {
        ...result,
        progress: (_.chain(result).values().sum().value() / _.values(result).length) * 100,
        isValid: _.chain(result).values().every().value(),
      };
    },
    consentTypeOptions() {
      return this.consentTypes.map((e, i) => {
        return { text: e, value: i };
      });
    },
  },
  async created() {
    await this.$store.dispatch("emailTemplates/getAll");
    this.designInterval = setInterval(async () => {
      if (!this.$refs.emailEditor) return;
      this.tempDesign = JSON.stringify(await this.$refs.emailEditor.saveDesign());
    }, 1000);
  },
  destroyed() {
    clearInterval(this.designInterval);
  },

  methods: {
    async applyTemplate(selectedEmailTemplate) {
      this.$emit("onSave");
      this.$vs.loading();

      // wait so the save applies first
      setTimeout(async () => {
        const dto = {
          venueCampaignId: this.venueCampaign.id,
          emailTemplateId: selectedEmailTemplate.id,
        };

        console.log("DTO", dto);

        await this.$store.dispatch("venueCampaigns/applyEmailTemplate", dto);

        this.$vs.loading.close();
      }, 200);
    },
    async onClickSave() {
      const emailExport = await this.$refs.emailEditor.export();

      this.venueCampaign.emailJson = JSON.stringify(emailExport.design);
      // this.venueCampaign.emailHtml = emailExport.html;

      this.$emit("onSave");
    },

    async onClickApplyTemplate() {
      await this.onClickSave();

      this.$refs.popupApplyEmailTemplate.show(this.venueCampaign.id);
    },

    async onClickSendTest() {
      await this.onClickSave();

      try {
        this.$vs.loading();
        await this.$store.dispatch("venueCampaigns/sendTestEmail", {
          venueCampaignId: this.venueCampaign.id,
        });
        this.$vs.notify({
          color: "green",
          title: "Test email sent",
          text: "A test email has been sent to your account",
        });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Error sending test email",
          text: err,
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.previewImageWrapper {
  height: 300px;
  overflow: hidden;
  border-radius: 8px;

  .previewImage {
    transform: scale(1.2) translateY(27px);
    object-fit: cover;
  }
}
</style>